import React from 'react';

import { MenuItem } from '@mui/material';
import Alert from '@mui/material/Alert';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { BarChart } from 'components/common/charts/barChart/barChart';
import { Pills } from 'components/common/pills/pills';

import { PaginationWithPageSize } from 'components/DesignSystem/Pagination/PaginationWithPageSize';
import { Select } from 'components/DesignSystem/Select';
import { useActivityLevelDriversLogic } from 'components/widgets/ActivityLevelDrivers/ActivityLevelDrivers.logic';
import classes from 'components/widgets/ActivityLevelDrivers/ActivityLevelDrivers.module.scss';
import { useActivityLevelDriversProvider } from 'components/widgets/ActivityLevelDrivers/ActivityLevelDrivers.provider';
import { impactTypeOptions } from 'components/widgets/ActivityLevelDrivers/ActivityLevelDrivers.utils';
import { ExportToCsvButton } from 'components/widgets/RiskRegister/components/ExportToCsvButton/ExportToCsvButton';

const title = 'Activity-Level Drivers';
const id = 'activityLevelDrivers';

export const ActivityLevelDrivers = ({
    widgetId,
    projectId,
    contractId,
    latestVersionId,
    setSubTitleForExport,
    activeTab,
}) => {
    const { api, ui } = useActivityLevelDriversProvider({ latestVersionId });

    const { logic } = useActivityLevelDriversLogic({ api, ui });

    const { subTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    return activeTab === 'chart' ? (
        <WidgetWithTitle
            title={title}
            titleComponents={[
                <ExportDropdown key={'export'} title={title} subTitle={subTitle}>
                    <MenuItem>
                        <ExportToCsvButton
                            headers={logic.exportCsv.headers}
                            rows={logic.exportCsv.rows}
                            fileName={logic.exportCsv.fileName}
                        />
                    </MenuItem>
                </ExportDropdown>,
            ]}
            titleFilters={
                <div className={classes.pills}>
                    <Pills>
                        Target Activity: <span className={classes.value}>{logic.taskVersionLabel}</span>
                    </Pills>
                    <Pills>
                        Number of iterations: <span className={classes.value}>{logic.numOfIterations}</span>
                    </Pills>
                </div>
            }
        >
            <div className={classes.chartLabels}>
                <div className={classes.activities}>Activities</div>
                <div>
                    <div className={classes.selectWrapper} key={'impactType'}>
                        <label className={classes.title}>Impact Type</label>
                        <Select<ISelectOption<'DAYS' | 'CORRELATION'>>
                            options={impactTypeOptions}
                            onChange={(value) => value && ui.setters.setImpactType(value)}
                            value={ui.state.impactType}
                        />
                    </div>
                </div>
            </div>
            <div className={classes.chart}>
                <BarChart
                    options={logic.options}
                    series={logic.series}
                    height={600}
                    noData={false}
                    isFetching={false}
                />
            </div>
            <div className={classes.pagination}>
                <PaginationWithPageSize
                    totalPages={logic.totalPages}
                    pagination={ui.state.pagination}
                    setPagination={ui.setters.setPagination}
                    showPageSizeOptions={false}
                />
            </div>
            <div>
                <Alert severity={'info'}>
                    <div className={classes.info}>
                        {ui.state.impactType.value == 'CORRELATION' ? (
                            <span>
                                Focus on mitigating the impact of risks to the activities with the highest sensitivity.
                                These are likely activities which are central to the completion of the target activity,
                                and currently have a high number of risks associated with them or their predecessors.{' '}
                                <br />
                                Mitigating the impact of Risks towards these specific activities will bring the P20, P50
                                and P80 dates forwards.
                            </span>
                        ) : (
                            <span>
                                Focus on mitigating the impact of risks to the activities that have the highest ‘impact
                                on target’. These are likely activities which are central to the completion of the
                                target activity, and currently have a high number of risks associated with them or their
                                predecessors.
                                <br /> Mitigating the impact of Risks towards these specific activities will bring the
                                P20, P50 and P80 dates forwards.
                            </span>
                        )}
                    </div>
                </Alert>
            </div>
        </WidgetWithTitle>
    ) : null;
};
